/**
 * Utility helpers for API Error.
 * This file contains shared pure helpers used by multiple screens, services, or wrappers.
 */
import { HttpErrorResponse } from '@angular/common/http';

const NETWORK_ERROR_MESSAGE = 'shared.toasts.networkError';
const OFFLINE_ERROR_MESSAGE = 'shared.toasts.networkError';
const SERVER_ERROR_MESSAGE = 'shared.toasts.genericError';

/**
 * BUSINESS LOGIC: getApiErrorMessage
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function getApiErrorMessage(err: unknown, fallback = 'shared.toasts.genericError'): string {
  if (isOffline()) {
    return OFFLINE_ERROR_MESSAGE;
  }

  if (err instanceof HttpErrorResponse) {
    const responseMessage = extractMessageValue(err.error);
    if (responseMessage) return responseMessage;

    if (err.status === 0) {
      return NETWORK_ERROR_MESSAGE;
    }

    if (err.status >= 500) {
      return SERVER_ERROR_MESSAGE;
    }
  }

  const responseMessage = extractResponseMessage(err);
  if (responseMessage) return responseMessage;

  if (err instanceof Error && err.message) {
    return normalizeBrowserErrorMessage(err.message, fallback);
  }

  if (typeof err === 'string' && err.trim()) {
    return err.trim();
  }

  return fallback;
}

/**
 * BUSINESS LOGIC: isOffline
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * BUSINESS LOGIC: extractResponseMessage
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function extractResponseMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return '';

  const errorBody = (err as { error?: unknown }).error;
  const bodyMessage = extractMessageValue(errorBody);
  if (bodyMessage) return bodyMessage;

  return extractMessageValue(err);
}

/**
 * BUSINESS LOGIC: extractMessageValue
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function extractMessageValue(value: unknown): string {
  if (typeof value === 'string') {
    const message = value.trim();
    return isHtmlErrorPage(message) ? '' : message;
  }
  if (Array.isArray(value)) {
    return value.map((item) => extractMessageValue(item)).filter(Boolean).join(' ');
  }
  if (!value || typeof value !== 'object') return '';

  const record = value as Record<string, unknown>;
  for (const key of ['message', 'error', 'detail', 'title']) {
    const message = extractMessageValue(record[key]);
    if (message) return message;
  }

  return '';
}

/**
 * BUSINESS LOGIC: normalizeBrowserErrorMessage
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function normalizeBrowserErrorMessage(message: string, fallback: string): string {
  const normalized = message.trim();
  if (!normalized) return fallback;
  if (isHtmlErrorPage(normalized)) return SERVER_ERROR_MESSAGE;

  const lower = normalized.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('load failed')) {
    return NETWORK_ERROR_MESSAGE;
  }

  return normalized;
}

/**
 * BUSINESS LOGIC: isHtmlErrorPage
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function isHtmlErrorPage(value: string): boolean {
  const lower = value.trimStart().toLowerCase();
  return lower.startsWith('<!doctype html') || lower.startsWith('<html') || /<\/?[a-z][\s\S]*>/i.test(value);
}
